import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, Upload, message } from 'antd';
import type { RcFile } from 'antd/lib/upload';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { useQueryClient } from 'react-query';
import { useSaveGroupGiftTrackingNumber } from 'hooks';
import { AnalysisSection, FileBox, StepCardList, UploadDesc, UploadModalFooter } from './styles';

const STEPS = [
  { no: '1단계', label: '주문 내역 엑셀 다운로드' },
  { no: '2단계', label: '송장번호·택배사 입력' },
  { no: '3단계', label: '파일 업로드' }
];

// 주문 데이터가 담긴 시트명 (메타정보/상품별 시트가 함께 있어 명시적으로 선택)
const ORDER_SHEET_NAME = '주문 내역';
// 엑셀 열 인덱스 (0-base): B=주문번호, Y=택배사코드, Z=송장번호
const COL_GROUP_GIFT_ORDER_IDX = 1;
const COL_LOGISTICS_CODE = 24;
const COL_TRACKING_NUMBER = 25;

interface IParsedRow {
  rowNumber: number;
  groupGiftOrderIdx: number;
  logisticsCode: string;
  trackingNumber: string;
}

interface IFailureRow {
  rowNumber: number;
  groupGiftOrderIdx: number;
  reason: string;
}

interface IRegisterResult {
  successCount: number;
  failures: IFailureRow[];
}

interface DeliveryUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const getCellString = (cell?: XLSX.CellObject) => (cell?.v === undefined || cell?.v === null ? '' : String(cell.v).trim());

const DeliveryUploadModal = ({ open, onClose }: DeliveryUploadModalProps) => {
  const [file, setFile] = useState<RcFile | null>(null);
  const [rows, setRows] = useState<IParsedRow[]>([]);
  const [result, setResult] = useState<IRegisterResult | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);

  const queryClient = useQueryClient();
  const { mutateAsync: saveTrackingNumber } = useSaveGroupGiftTrackingNumber();

  useEffect(() => {
    if (!open) {
      setFile(null);
      setRows([]);
      setResult(null);
      setIsRegistering(false);
    }
  }, [open]);

  const handleBeforeUpload = (selected: RcFile) => {
    const isValidExtension = /\.(xlsx|csv)$/i.test(selected.name);
    if (!isValidExtension) {
      message.warning('.xlsx, .csv 파일만 업로드할 수 있습니다.');
      return false;
    }

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      const workbook = XLSX.read(target?.result, { type: 'array' });
      const sheetName = workbook.SheetNames.includes(ORDER_SHEET_NAME) ? ORDER_SHEET_NAME : workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1');

      const parsed: IParsedRow[] = [];
      for (let { r } = range.s; r <= range.e.r; r += 1) {
        const rawIdx = sheet[XLSX.utils.encode_cell({ c: COL_GROUP_GIFT_ORDER_IDX, r })]?.v;
        // 주문번호(B)가 숫자인 행만 데이터로 취급 (헤더/빈 행 제외)
        const isDataRow = rawIdx !== undefined && rawIdx !== null && rawIdx !== '' && !Number.isNaN(Number(rawIdx));
        if (isDataRow) {
          parsed.push({
            rowNumber: r + 1,
            groupGiftOrderIdx: Number(rawIdx),
            logisticsCode: getCellString(sheet[XLSX.utils.encode_cell({ c: COL_LOGISTICS_CODE, r })]),
            trackingNumber: getCellString(sheet[XLSX.utils.encode_cell({ c: COL_TRACKING_NUMBER, r })])
          });
        }
      }

      if (!parsed.length) {
        message.warning('업로드할 데이터가 없습니다. 엑셀 내용을 확인해주세요.');
        return;
      }

      setFile(selected);
      setRows(parsed);
      setResult(null);
    };
    reader.readAsArrayBuffer(selected);
    return false;
  };

  const handleRegister = async () => {
    if (!rows.length || isRegistering) {
      return;
    }
    setIsRegistering(true);

    // 행 단위로 순차 등록 — 성공 행은 노출하지 않고, 실패 행만 사유와 함께 수집
    const failures: IFailureRow[] = [];
    for (let i = 0; i < rows.length; i += 1) {
      const { rowNumber, groupGiftOrderIdx, trackingNumber, logisticsCode } = rows[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await saveTrackingNumber({ groupGiftOrderIdx, trackingNumber, logisticsCode });
      } catch (error) {
        const reason = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
        failures.push({ rowNumber, groupGiftOrderIdx, reason: reason || '등록에 실패했습니다.' });
      }
    }

    setResult({ successCount: rows.length - failures.length, failures });
    setIsRegistering(false);
    queryClient.invalidateQueries(['getGroupGiftOrders']);
    if (!failures.length) {
      message.success('배송정보가 모두 등록되었습니다.');
    }
  };

  const handleDownloadFailures = () => {
    if (!result?.failures.length) {
      return;
    }
    const sheetData = result.failures.map(({ rowNumber, groupGiftOrderIdx, reason }) => ({
      행: rowNumber,
      주문번호: groupGiftOrderIdx,
      '실패 사유': reason
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '실패 내역');
    XLSX.writeFile(workbook, `배송정보_업로드_실패내역_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  return (
    <Modal
      title="배송정보 업로드"
      open={open}
      width={700}
      onCancel={onClose}
      footer={
        <UploadModalFooter>
          <Button onClick={handleDownloadFailures} disabled={!result?.failures.length}>
            실패 내역 엑셀 다운로드
          </Button>
          <div className="footer-right">
            <Button onClick={onClose}>닫기</Button>
            <Button type="primary" disabled={!file || !!result} loading={isRegistering} onClick={handleRegister}>
              등록
            </Button>
          </div>
        </UploadModalFooter>
      }
    >
      <Spin spinning={isRegistering} tip="배송정보를 등록하고 있습니다...">
        <UploadDesc>주문 내역 엑셀에 송장번호·택배사를 입력해 업로드하면 일괄 등록됩니다.</UploadDesc>
        <StepCardList>
          {STEPS.map(({ no, label }) => (
            <li key={no}>
              <div className="step-no">{no}</div>
              <div className="step-label">{label}</div>
            </li>
          ))}
        </StepCardList>
        <FileBox>
          <div>
            <span className="file-name">{file ? file.name : '파일을 선택해주세요'}</span>
            <span className="file-hint">{file ? `${rows.length}행 · .xlsx, .csv 파일만 가능` : '.xlsx, .csv 파일만 가능'}</span>
          </div>
          <Upload accept=".xlsx,.csv" maxCount={1} showUploadList={false} beforeUpload={handleBeforeUpload}>
            <Button>{file ? '파일 변경' : '파일 선택'}</Button>
          </Upload>
        </FileBox>
        {result ? (
          <AnalysisSection>
            <div className="analysis-header">
              <span className="analysis-title">등록 결과</span>
              <span className="success">성공 {result.successCount}건</span>
              <span className="fail">실패 {result.failures.length}건</span>
            </div>
            {result.failures.length ? (
              <div className="analysis-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '20%' }}>행</th>
                      <th style={{ width: '25%' }}>주문번호</th>
                      <th>실패 사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.failures.map(({ rowNumber, groupGiftOrderIdx, reason }) => (
                      <tr key={`${rowNumber}-${groupGiftOrderIdx}`}>
                        <td>{rowNumber}</td>
                        <td>{groupGiftOrderIdx}</td>
                        <td className="fail-reason">{reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </AnalysisSection>
        ) : null}
      </Spin>
    </Modal>
  );
};

export default DeliveryUploadModal;
